/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes, useMemo, useState } from 'preact/compat';
import {
  classes,
  generateSrcset,
  ResolveImageUrlOptions,
} from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/Image/Image.css';

export interface ImageProps extends HTMLAttributes<HTMLDivElement> {
  params?: ResolveImageUrlOptions;
  src: string;
  sizes?: string;
  onLoad?: (e: any) => void;
}
export const Image: FunctionComponent<ImageProps> = ({
  className,
  src,
  params,
  loading = 'lazy',
  srcSet,
  sizes,
  onLoad,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);

  const _srcSet = useMemo(() => {
    // If srcSet is provided, use it
    if (srcSet) return srcSet;

    // If src is not provided, return "undefined"
    if (!src || !params) return;

    // Otherwise, generate srcSet based on params
    return generateSrcset(src, { ...params });
  }, [params, src, srcSet]);

  // Add class at image onLoad
  const onLoadHandler = (e: any) => {
    setLoaded(true);    
    onLoad?.(e);
  };

  return (
    <img
      {...props}
      className={classes([
        'dropin-image',
        ['dropin-image--loaded', loaded],
        className,
      ])}
      loading={loading}
      onLoad={onLoadHandler}
      src={src}
      srcSet={_srcSet}
      sizes={sizes}
    />
  );
};
